import React, { useState, useEffect, useRef } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Form,
  Stack,
  Tabs,
  Tab,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaEye,
  FaSearch,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaMapMarkerAlt,
  FaFileExcel,
  FaDownload,
  FaFilter,
  FaInfoCircle,
  FaSync,
} from "react-icons/fa";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getFirestore,
  doc,
  getDoc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import firebaseApp from "../../firebase/credenciales";
import { getAuth } from "firebase/auth"; // ✅ Nueva importación para Firebase v9+
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import {
  cargarAlcanceGrupoAsistencia,
  filtrarPorDnisPermitidos,
} from "./groupScope";

// ✅ Nueva importación para generar PDFs masivos
import { generateControlAsistencia, generateControlAsistenciaMasiva } from "./GenerarReportePDF"; // ✅ Cambiar a generateControlAsistencia
import GenerarReportePDF from "./GenerarReportePDF"; // ✅ Nueva importación

const db = getFirestore(firebaseApp);
const normalizeTextToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
const normalizeDni = (value) => String(value || "").trim();
const extractMemberDni = (member) => {
  if (typeof member === "string" || typeof member === "number") {
    return normalizeDni(member);
  }

  if (member && typeof member === "object") {
    return normalizeDni(member.dni || member.id || member.uid || member.userId);
  }

  return "";
};
const normalizeMembers = (members = []) =>
  Array.from(
    new Set(
      (Array.isArray(members) ? members : [])
        .map((member) => extractMemberDni(member))
        .filter(Boolean)
    )
  );
const isGlobalScopeRole = (role) => {
  const normalized = normalizeTextToken(role);
  return (
    normalized === "superadmin" ||
    normalized === "admin" ||
    normalized === "administrador"
  );
};

function ReporteAsistenciaPDF({ usuario }) {
  const [reportes, setReportes] = useState([]);
  const [todosLosUsuarios, setTodosLosUsuarios] = useState([]);
  const [usuariosConReportes, setUsuariosConReportes] = useState([]);
  const [usuariosSinReportes, setUsuariosSinReportes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busquedaDni, setBusquedaDni] = useState("");
  const [periodoFiltro, setPeriodoFiltro] = useState(""); // Ahora almacena el valor como "2025-01"
  const [activeTab, setActiveTab] = useState("con-reportes");
  const [grupos, setGrupos] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState("todos");
  const [alcanceGrupo, setAlcanceGrupo] = useState({
    restringidoPorGrupo: false,
    grupoAlcance: null,
  });
  const [dnisPermitidos, setDnisPermitidos] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [gruposAsignadosUsuario, setGruposAsignadosUsuario] = useState([]);
  const activeRequestIdRef = useRef(0);

  useEffect(() => {
    if (usuario?.ruc && !scopeLoading) {
      if (busquedaDni.trim()) {
        buscarPorDni();
      } else {
        obtenerDatosCompletos(periodoFiltro);
      }
    }
  }, [usuario, periodoFiltro, scopeLoading]);

  // Cargar alcance por grupo según el usuario autenticado
  useEffect(() => {
    const cargarScope = async () => {
      if (!usuario?.ruc) return;
      setScopeLoading(true);
      try {
        const scope = await cargarAlcanceGrupoAsistencia({
          db,
          rucEmpresa: usuario.ruc,
          usuario,
        });
        setGrupos(scope.gruposDisponibles);
        setGrupoSeleccionado(scope.grupoFijoId || "todos");
        setDnisPermitidos(scope.dnisPermitidos);
        setAlcanceGrupo({
          restringidoPorGrupo: scope.restringidoPorGrupo,
          grupoAlcance: scope.grupoAlcance || null,
        });

        const esRolGlobal = isGlobalScopeRole(usuario?.rol);
        if (!esRolGlobal && usuario?.dni) {
          try {
            const [membershipDoc, gruposSnap] = await Promise.all([
              getDoc(doc(db, `grupos/${String(usuario.ruc)}/memberships`, String(usuario.dni).trim())),
              getDocs(collection(db, `grupos/${String(usuario.ruc)}/grupos`)),
            ]);
            const gruposEmpresa = gruposSnap.docs.map((groupDoc) => ({
              id: groupDoc.id,
              name: groupDoc.data()?.name || "Grupo sin nombre",
              members: normalizeMembers(groupDoc.data()?.members),
            }));

            const membershipData = membershipDoc.exists() ? (membershipDoc.data() || {}) : {};
            const singleGroupId = String(membershipData?.groupId || "").trim();
            const multipleGroupIds = Array.isArray(membershipData?.groupIds)
              ? membershipData.groupIds.map((id) => String(id || "").trim()).filter(Boolean)
              : [];
            const groupIdsDesdeMembership = Array.from(
              new Set([singleGroupId, ...multipleGroupIds].filter(Boolean))
            );
            const dniStr = normalizeDni(usuario.dni);
            const groupIdsDesdeMembers = gruposEmpresa
              .filter((g) => (g.members || []).includes(dniStr))
              .map((g) => String(g.id));
            const groupIds = Array.from(
              new Set([...groupIdsDesdeMembership, ...groupIdsDesdeMembers])
            );

            const gruposAsignados = gruposEmpresa.filter((g) =>
              groupIds.includes(String(g.id))
            );
            setGruposAsignadosUsuario(gruposAsignados);
            if (gruposAsignados.length > 0) {
              setGrupos(gruposAsignados);
              const dnisAsignados = Array.from(
                new Set(gruposAsignados.flatMap((g) => normalizeMembers(g.members || [])))
              );
              setDnisPermitidos(new Set(dnisAsignados));
              setGrupoSeleccionado((prev) => {
                if (prev && gruposAsignados.some((g) => String(g.id) === String(prev))) {
                  return prev;
                }
                return gruposAsignados[0].id;
              });
            }
          } catch (membershipError) {
            console.error("Error al resolver grupos asignados del usuario:", membershipError);
            setGruposAsignadosUsuario([]);
          }
        } else {
          setGruposAsignadosUsuario([]);
        }

        if (scope.restringidoPorGrupo && scope.gruposDisponibles.length === 0) {
          setError("Tu usuario no tiene un grupo asignado para este módulo.");
        }
      } catch (e) {
        console.error("Error al cargar alcance de grupos:", e);
        setGrupos([]);
        setGrupoSeleccionado("todos");
        setDnisPermitidos(new Set());
        setGruposAsignadosUsuario([]);
      } finally {
        setScopeLoading(false);
      }
    };
    cargarScope();
  }, [usuario]);

  // Función auxiliar para truncar nombres de hojas de Excel
  const truncarNombreHoja = (nombre, maxLength = 31) => {
    if (nombre.length <= maxLength) return nombre;
    return nombre.substring(0, maxLength - 3) + "...";
  };

  // ✅ Nueva función para obtener la etiqueta del período a partir del valor
  const getPeriodoEtiqueta = (valor) => {
    if (!valor) return "";
    const [año, mes] = valor.split("-");
    const nombresMeses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    return `${nombresMeses[parseInt(mes) - 1] || mes} ${año || ""}`.trim();
  };

  const formatUltimoReporteFecha = (reporte, periodoFiltroVal) => {
    if (periodoFiltroVal) return getPeriodoEtiqueta(periodoFiltroVal);
    if (!reporte) return "Sin Reporte";

    const nombresMeses = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    const nMes = String(reporte.nombre_mes || "").trim();
    const sAño = String(reporte.año || "").trim();

    if (nMes && sAño && nMes.toLowerCase() !== "undefined" && sAño.toLowerCase() !== "undefined") {
      return `${nMes} ${sAño}`;
    }

    if (reporte.mes && reporte.año && !isNaN(reporte.mes)) {
      const mNum = parseInt(reporte.mes, 10);
      if (mNum >= 1 && mNum <= 12) {
        return `${nombresMeses[mNum - 1]} ${reporte.año}`;
      }
    }

    if (reporte.created_at) {
      try {
        const date = reporte.created_at.toDate ? reporte.created_at.toDate() : new Date(reporte.created_at);
        if (!isNaN(date.getTime())) {
          return `${nombresMeses[date.getMonth()]} ${date.getFullYear()}`;
        }
      } catch (e) {}
    }

    return "Reporte Registrado";
  };

  const formatNombreTrabajador = (u) => {
    if (!u) return "Trabajador";

    const cleanStr = (val) => {
      const s = String(val || "").trim();
      return s && s.toLowerCase() !== "n/a" && s.toLowerCase() !== "undefined" && s.toLowerCase() !== "null" ? s : "";
    };

    const nombre = cleanStr(u.nombres) || cleanStr(u.name) || cleanStr(u.nombre) || cleanStr(u.primerNombre);
    const apellidos = cleanStr(u.apellidos) || `${cleanStr(u.apellidoPaterno)} ${cleanStr(u.apellidoMaterno)}`.trim() || cleanStr(u.apellido);

    if (apellidos && nombre) {
      return `${apellidos}, ${nombre}`;
    }
    if (apellidos || nombre) {
      return apellidos || nombre;
    }
    return `Trabajador DNI: ${u.dni || "S/D"}`;
  };

  // Caché en memoria para obtenerDatosUsuario — evita N+1 queries
  const usuarioCacheRef = React.useRef(new Map());

  const obtenerDatosUsuario = async (dni, usuariosMapFallback = null) => {
    try {
      if (!dni) return null;
      const dniLimpio = String(dni).trim();
      if (!dniLimpio) return null;

      // 0. Buscar en caché en memoria (evita TODAS las queries)
      if (usuarioCacheRef.current.has(dniLimpio)) {
        return usuarioCacheRef.current.get(dniLimpio);
      }

      // 0b. Buscar en el Map de usuarios ya descargado (evita queries a Firestore)
      if (usuariosMapFallback && usuariosMapFallback.has(dniLimpio)) {
        const cached = usuariosMapFallback.get(dniLimpio);
        usuarioCacheRef.current.set(dniLimpio, cached);
        return cached;
      }

      const rucStr = String(usuario?.ruc || "").trim();
      const usersRef = collection(db, "users");

      // 1. Buscar por rucEmpresa + dni (string) — la query más probable
      let userSnap = await getDocs(
        query(usersRef, where("rucEmpresa", "==", rucStr), where("dni", "==", dniLimpio), limit(1))
      );

      // 2. Si vacío y es numérico, buscar por dni number
      if (userSnap.empty && !isNaN(dniLimpio)) {
        userSnap = await getDocs(
          query(usersRef, where("rucEmpresa", "==", rucStr), where("dni", "==", parseInt(dniLimpio, 10)), limit(1))
        );
      }

      // 3. Buscar como ID de documento directo (sinwhere — acceso directo)
      if (userSnap.empty) {
        try {
          const userDoc = await getDoc(doc(db, "users", dniLimpio));
          if (userDoc.exists()) {
            userSnap = { empty: false, docs: [userDoc] };
          }
        } catch (e) {}
      }

      if (!userSnap.empty) {
        const userDoc = userSnap.docs[0];
        const data = userDoc.data() || {};
        const nombre = data.nombres || data.name || data.nombre || "";
        const apellidos = data.apellidos || `${data.apellidoPaterno || ''} ${data.apellidoMaterno || ''}`.trim() || "";

        const resultado = {
          id: userDoc.id,
          ...data,
          dni: String(data.dni || dniLimpio).trim(),
          name: nombre || "Trabajador",
          nombres: nombre || "Trabajador",
          apellidos: apellidos || "",
          area: data.area || data.areaNombre || data.unidadOrganizativa || "",
          cargo: data.cargo || data.puesto || "",
        };
        usuarioCacheRef.current.set(dniLimpio, resultado);
        return resultado;
      }

      // 4. Fallback mínimo: asistencia/{dni}/registros (1 query en lugar de 5)
      try {
        const regSnap = await getDocs(query(collection(db, `asistencia/${dniLimpio}/registros`), limit(5)));
        if (!regSnap.empty) {
          for (const d of regSnap.docs) {
            const rd = d.data() || {};
            if (rd.nombre || rd.apellidos) {
              const resultado = {
                id: dniLimpio,
                dni: dniLimpio,
                name: rd.nombre || "Trabajador",
                nombres: rd.nombre || "Trabajador",
                apellidos: rd.apellidos || "",
                area: rd.ceco || "",
                cargo: rd.cargo || "",
              };
              usuarioCacheRef.current.set(dniLimpio, resultado);
              return resultado;
            }
          }
        }
      } catch (e) {}

      return null;
    } catch (err) {
      console.error("Error al obtener datos del usuario:", err);
      return null;
    }
  };
  // Nueva función para obtener coordenadas activas del usuario
  const obtenerCoordenadasUsuario = async (dni) => {
    try {
      const coordenadasRef = collection(db, `asistencia/${dni}/coordenadas`);
      const q = query(
        coordenadasRef,
        orderBy("prioridad", "asc"),
        limit(1) // Solo obtener la coordenada con mayor prioridad
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const coordenada = snapshot.docs[0].data();
        return {
          nombrePlanta: coordenada.nombre || "N/A",
          empresa: coordenada.empresa || "N/A",
          latitud: coordenada.latitud,
          longitud: coordenada.longitud,
          radio: coordenada.radio,
        };
      }

      return null;
    } catch (err) {
      console.error(`Error al obtener coordenadas para DNI ${dni}:`, err);
      return null;
    }
  };

  // Nueva función para verificar si existen registros de asistencia (marcaciones)
  const verificarRegistrosAsistencia = async (dni, año, mes) => {
    try {
      const registrosRef = collection(db, `asistencia/${dni}/registros`);
      
      // Buscar registros del período especificado
      const q = query(
        registrosRef,
        orderBy("timestamp", "desc"),
        limit(500) // Obtener más registros para filtrar
      );

      const snapshot = await getDocs(q);
      
      console.log(`📊 Total de registros encontrados en asistencia/${dni}/registros: ${snapshot.size}`);
      
      if (!snapshot.empty) {
        // Filtrar registros del período especificado
        const registrosPeriodo = snapshot.docs.filter((doc) => {
          const docId = doc.id; // Formato: "2025-11-03_ingreso_080000_regularizacion"
          
          // Extraer fecha del ID del documento
          const [fechaParte] = docId.split("_");
          if (!fechaParte || fechaParte.length !== 10) {
            console.warn(`Formato de ID inesperado: ${docId}`);
            return false;
          }
          
          const [añoDoc, mesDoc] = fechaParte.split("-");
          
          const coincide = parseInt(añoDoc) === parseInt(año) && 
                          parseInt(mesDoc) === parseInt(mes);
          
          return coincide;
        });

        console.log(`📅 Registros en ${año}-${mes}: ${registrosPeriodo.length}`);

        if (registrosPeriodo.length > 0) {
          console.log(`✅ Se encontraron ${registrosPeriodo.length} registros de marcación para ${dni} en ${año}-${mes}`);
          return true;
        }
      }

      console.log(`❌ No se encontraron registros para ${dni} en ${año}-${mes}`);
      return false;
    } catch (err) {
      console.error(`Error al verificar registros de asistencia para DNI ${dni}:`, err);
      // Si hay error pero es por que la colección no existe, retornar false
      if (err.code === 'permission-denied' || err.code === 'not-found') {
        console.warn(`La colección asistencia/${dni}/registros no existe o no se puede acceder`);
        return false;
      }
      return false;
    }
  };

  // Función helper para generar opciones de período
  const generarOpcionesPeriodo = () => {
    const opciones = [];
    const fechaActual = new Date();
    const añoActual = fechaActual.getFullYear();

    // Generar opciones para los últimos 2 años
    for (let año = añoActual; año >= añoActual - 1; año--) {
      for (let mes = 12; mes >= 1; mes--) {
        if (año === añoActual && mes > fechaActual.getMonth() + 1) continue;
        const mesFormateado = mes.toString().padStart(2, "0");
        const valor = `${año}-${mesFormateado}`;

        // Crear nombres de meses en español
        const nombresMeses = [
          "Enero",
          "Febrero",
          "Marzo",
          "Abril",
          "Mayo",
          "Junio",
          "Julio",
          "Agosto",
          "Septiembre",
          "Octubre",
          "Noviembre",
          "Diciembre",
        ];

        const etiqueta = `${nombresMeses[mes - 1]} ${año}`;
        opciones.push({ valor, etiqueta });
      }
    }

    return opciones;
  };

  // Función para obtener nombres de archivo con período
  const obtenerNombreArchivo = (tipoReporte) => {
    const fechaActual = new Date().toISOString().split("T")[0];
    const empresaTexto = usuario.empresa
      ? usuario.empresa.replace(/[^a-zA-Z0-9]/g, "_")
      : "Empresa";

    const periodoTexto = periodoFiltro
      ? `_${getPeriodoEtiqueta(periodoFiltro)}`
      : "";

    switch (tipoReporte) {
      case "con-reportes":
        const dniTextoConReportes = busquedaDni ? `_DNI-${busquedaDni}` : "";
        return `Usuarios_Con_Reportes_${empresaTexto}${periodoTexto}${dniTextoConReportes}_${fechaActual}.xlsx`;

      case "sin-reportes":
        const dniTextoSinReportes = busquedaDni ? `_DNI-${busquedaDni}` : "";
        return `Usuarios_Sin_Reportes_${empresaTexto}${periodoTexto}${dniTextoSinReportes}_${fechaActual}.xlsx`;

      case "busqueda":
        return `Busqueda_Reportes_DNI_${busquedaDni}${periodoTexto}_${fechaActual}.xlsx`;

      default:
        return `Reporte_${fechaActual}.xlsx`;
    }
  };

  // Función auxiliar para filtrar usuarios por grupo
  const filtrarPorGrupo = (usuarios) => {
    if (grupoSeleccionado === "todos") return usuarios;
    const grupo = grupos.find((g) => String(g.id) === String(grupoSeleccionado));
    if (!grupo) return [];
    const membersSet = new Set(normalizeMembers(grupo.members || []));
    return usuarios.filter((u) => membersSet.has(normalizeDni(u.dni)));
  };

  // Función para obtener datos filtrados para usuarios con reportes
  const obtenerDatosConReportesFiltrados = () => {
    let datosFiltrados = [...usuariosConReportes];

    // Filtrar por DNI si está presente
    if (busquedaDni.trim()) {
      datosFiltrados = datosFiltrados.filter((usuario) =>
        usuario.dni.includes(busquedaDni.trim())
      );
    }

    // Filtrar por período si está presente
    if (periodoFiltro) {
      const [añoFiltro, mesFiltro] = periodoFiltro.split("-");
      const mesNumerico = parseInt(mesFiltro).toString();

      datosFiltrados = datosFiltrados.filter((usuario) => {
        const reporte = usuario.ultimoReporte;
        return (
          reporte && reporte.año === añoFiltro && reporte.mes === mesNumerico
        );
      });
    }

    // Aplica filtro por grupo
    datosFiltrados = filtrarPorGrupo(datosFiltrados);

    return datosFiltrados;
  };

  // Función para obtener datos filtrados para usuarios sin reportes
  const obtenerDatosSinReportesFiltrados = () => {
    let datosFiltrados = [...usuariosSinReportes];

    // Filtrar por DNI si está presente
    if (busquedaDni.trim()) {
      datosFiltrados = datosFiltrados.filter((usuario) =>
        usuario.dni.includes(busquedaDni.trim())
      );
    }

    // Nota: Los usuarios sin reportes no pueden filtrarse por período
    // ya que no tienen reportes en ningún período

    // Aplica filtro por grupo
    datosFiltrados = filtrarPorGrupo(datosFiltrados);

    return datosFiltrados;
  };

  // Función corregida para descargar PDFs en ZIP
  const descargarPDFsEnZip = async () => {
    const datosFiltrados = obtenerDatosConReportesFiltrados();

    if (datosFiltrados.length === 0) {
      alert("No hay PDFs para descargar con los filtros aplicados");
      return;
    }

    setLoading(true); // Mostrar indicador de carga

    try {
      const zip = new JSZip();
      const folder = zip.folder("Reportes_PDF");

      // ✅ Usa getAuth para obtener la instancia de autenticación
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;

      if (!user) {
        alert("Debes estar autenticado para descargar PDFs.");
        setLoading(false);
        return;
      }

      // Verificación inicial: Contar usuarios con URLs válidas
      const usuariosConUrl = datosFiltrados.filter(
        (u) =>
          u.ultimoReporte?.pdf_url &&
          typeof u.ultimoReporte.pdf_url === "string" &&
          u.ultimoReporte.pdf_url.trim() !== ""
      );

      if (usuariosConUrl.length === 0) {
        alert(
          "Ningún usuario tiene una URL de PDF válida. Verifica los datos en Firestore."
        );
        setLoading(false);
        return;
      }

      // Procesar en lotes para evitar sobrecarga (máximo 5 concurrentes)
      const batchSize = 5;
      for (let i = 0; i < usuariosConUrl.length; i += batchSize) {
        const batch = usuariosConUrl.slice(i, i + batchSize);
        const promises = batch.map(async (usuario) => {
          const pdfUrl = usuario.ultimoReporte.pdf_url;

          try {
            // ✅ Extrae la ruta del archivo de la URL original
            const urlParts = pdfUrl.split("/o/")[1]?.split("?")[0];
            if (!urlParts) {
              throw new Error(
                "URL inválida: no se pudo extraer la ruta del archivo"
              );
            }
            const decodedPath = decodeURIComponent(urlParts); // Decodifica la ruta

            // ✅ Usa getDownloadURL para obtener una URL fresca con token válido
            const storage = getStorage(firebaseApp);
            const fileRef = ref(storage, decodedPath);
            const freshUrl = await getDownloadURL(fileRef);

            // ✅ Fetch simple con la URL fresca (sin headers manuales)
            const response = await fetch(freshUrl, {
              method: "GET",
              // No necesitas 'credentials' ni headers de autenticación manual
            });

            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}: ${response.statusText}`
              );
            }

            const blob = await response.blob();
            if (blob.size === 0) {
              throw new Error("El archivo PDF está vacío");
            }

            const nombreArchivo = `${usuario.dni}_${
              usuario.name || "SinNombre"
            }_${usuario.apellidos || "SinApellido"}.pdf`;
            folder.file(nombreArchivo, blob);
          } catch (error) {
            console.error(
              `Error descargando PDF para DNI ${usuario.dni}:`,
              error
            );
            // Crear archivo TXT solo si es útil (opcional)
            const errorMsg = `No se pudo descargar el PDF para DNI ${usuario.dni}. Error: ${error.message}`;
            folder.file(`Error_${usuario.dni}.txt`, errorMsg);
          }
        });

        await Promise.all(promises); // Espera a que termine el lote
      }

      // Generar y descargar el ZIP
      const content = await zip.generateAsync({ type: "blob" });
      const nombreZip = `Reportes_PDF_${
        new Date().toISOString().split("T")[0]
      }.zip`;
      saveAs(content, nombreZip);

      alert(
        `ZIP descargado exitosamente. Procesados: ${usuariosConUrl.length} usuarios. Revisa la consola para detalles de errores.`
      );
    } catch (error) {
      console.error("Error al crear el ZIP:", error);
      alert(
        "Error al generar el archivo ZIP. Revisa la consola para más detalles."
      );
    } finally {
      setLoading(false);
    }
  };

  // Función modificada para exportar usuarios con reportes a Excel
  const exportarConReportesExcel = () => {
    const datosFiltrados = obtenerDatosConReportesFiltrados();

    if (datosFiltrados.length === 0) {
      alert("No hay datos para exportar con los filtros aplicados");
      return;
    }

    try {
      // Preparar datos para Excel
      const datosExcel = datosFiltrados.map((usuario, index) => ({
        "N°": index + 1,
        DNI: usuario.dni,
        Nombres: usuario.name || "N/A",
        Apellidos: usuario.apellidos || "N/A",
        Planta: usuario.coordenadas?.nombrePlanta || "Sin asignar",
        "Radio (m)": usuario.coordenadas?.radio || "N/A",
        Mes: usuario.ultimoReporte?.nombre_mes || "N/A",
        Año: usuario.ultimoReporte?.año || "N/A",
        "Fecha de Creación": usuario.ultimoReporte
          ? formatearFechaExcel(usuario.ultimoReporte.created_at)
          : "N/A",
        Empresa: usuario.coordenadas?.empresa || "N/A",
      }));

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 5 }, // N°
        { wch: 12 }, // DNI
        { wch: 20 }, // Nombres
        { wch: 20 }, // Apellidos
        { wch: 25 }, // Planta
        { wch: 10 }, // Radio
        { wch: 12 }, // Mes
        { wch: 8 }, // Año
        { wch: 25 }, // Fecha de Creación
        { wch: 25 }, // Empresa
      ];
      ws["!cols"] = colWidths;

      // Construir nombre de hoja de forma más compacta
      let nombreHoja = "Con Reportes";

      // Agregar filtros de forma compacta
      if (periodoFiltro || busquedaDni.trim()) {
        const filtros = [];
        if (periodoFiltro) {
          filtros.push(getPeriodoEtiqueta(periodoFiltro));
        }
        if (busquedaDni.trim()) {
          filtros.push(`DNI-${busquedaDni.trim()}`);
        }
        nombreHoja += ` (${filtros.join(", ")})`;
      }

      // Truncar el nombre si excede 31 caracteres
      nombreHoja = truncarNombreHoja(nombreHoja);

      // Añadir hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

      // Descargar archivo con nombre actualizado
      XLSX.writeFile(wb, obtenerNombreArchivo("con-reportes"));

      // Mostrar mensaje de éxito
      alert(
        `Excel descargado exitosamente con ${datosFiltrados.length} registro(s)`
      );
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al generar el archivo Excel");
    }
  };

  // Función modificada para exportar usuarios sin reportes a Excel
  const exportarSinReportesExcel = () => {
    const datosFiltrados = obtenerDatosSinReportesFiltrados();

    if (datosFiltrados.length === 0) {
      alert("No hay datos para exportar con los filtros aplicados");
      return;
    }

    try {
      // Obtener información del período para mostrar en el reporte
      let periodoInfo = "Todos los períodos";
      if (periodoFiltro) {
        periodoInfo = getPeriodoEtiqueta(periodoFiltro);
      }

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();

      // Crear array completo con información del reporte y datos
      const datosCompletos = [
        // Encabezado del reporte
        [`REPORTE DE USUARIOS SIN REPORTES - ${usuario.empresa || "EMPRESA"}`],
        [`Período consultado: ${periodoInfo}`],
        [`Fecha de generación: ${new Date().toLocaleDateString("es-PE")}`],
        [`Total de usuarios sin reportes: ${datosFiltrados.length}`],
        [], // Fila vacía para separar

        // Encabezados de las columnas
        [
          "N°",
          "DNI",
          "Nombres",
          "Apellidos",
          "Planta",
          "Radio (m)",
          "Estado",
          "Período Consultado",
          "Empresa",
          "Observaciones",
        ],

        // Datos de los usuarios
        ...datosFiltrados.map((usuario, index) => [
          index + 1,
          usuario.dni,
          usuario.name || "N/A",
          usuario.apellidos || "N/A",
          usuario.coordenadas?.nombrePlanta || "Sin asignar",
          usuario.coordenadas?.radio || "N/A",
          "Sin Reporte",
          periodoInfo,
          usuario.coordenadas?.empresa || "N/A",
          periodoFiltro
            ? `No tiene reporte para ${periodoInfo}`
            : "No tiene reportes en ningún período",
        ]),
      ];

      // Crear hoja con todos los datos
      const ws = XLSX.utils.aoa_to_sheet(datosCompletos);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 5 }, // N°
        { wch: 12 }, // DNI
        { wch: 20 }, // Nombres
        { wch: 20 }, // Apellidos
        { wch: 25 }, // Planta
        { wch: 10 }, // Radio
        { wch: 15 }, // Estado
        { wch: 20 }, // Período Consultado
        { wch: 25 }, // Empresa
        { wch: 35 }, // Observaciones
      ];
      ws["!cols"] = colWidths;

      // Aplicar estilos al encabezado (opcional - requiere más configuración)
      // Combinar celdas para el título principal
      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push(
        { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }, // Título principal
        { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } }, // Período
        { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } }, // Fecha
        { s: { r: 3, c: 0 }, e: { r: 3, c: 9 } } // Total
      );

      // Construir nombre de hoja de forma más compacta
      let nombreHoja = "Sin Reportes";

      // Agregar información del período al nombre de la hoja
      if (periodoFiltro) {
        nombreHoja += ` ${getPeriodoEtiqueta(periodoFiltro)}`;
      }

      // Agregar filtro de DNI si existe
      if (busquedaDni.trim()) {
        nombreHoja += ` (DNI-${busquedaDni.trim()})`;
      }

      // Truncar el nombre si excede 31 caracteres
      nombreHoja = truncarNombreHoja(nombreHoja);

      // Añadir hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

      // Descargar archivo con nombre actualizado
      XLSX.writeFile(wb, obtenerNombreArchivo("sin-reportes"));

      // Mostrar mensaje de éxito con información del período
      const mensajeExito = periodoFiltro
        ? `Excel descargado exitosamente con ${datosFiltrados.length} usuario(s) sin reportes para ${periodoInfo}`
        : `Excel descargado exitosamente con ${datosFiltrados.length} usuario(s) sin reportes`;

      alert(mensajeExito);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al generar el archivo Excel");
    }
  };

  // Función modificada para exportar reporte de búsqueda específica a Excel
  const exportarBusquedaExcel = () => {
    // Para búsqueda específica, usar directamente el estado 'reportes'
    if (reportes.length === 0) {
      alert("No hay datos para exportar");
      return;
    }

    try {
      // Preparar datos para Excel
      const datosExcel = reportes.map((reporte, index) => ({
        "N°": index + 1,
        DNI: reporte.dni,
        Nombres: reporte.nombres,
        Apellidos: reporte.apellidos,
        Planta: reporte.coordenadas?.nombrePlanta || "Sin asignar",
        "Radio (m)": reporte.coordenadas?.radio || "N/A",
        Mes: reporte.nombre_mes,
        Año: reporte.año,
        "Fecha de Creación": formatearFechaExcel(reporte.created_at),
        Empresa: reporte.coordenadas?.empresa || "N/A",
        "URL PDF": reporte.pdf_url || "N/A",
      }));

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 5 }, // N°
        { wch: 12 }, // DNI
        { wch: 20 }, // Nombres
        { wch: 20 }, // Apellidos
        { wch: 25 }, // Planta
        { wch: 10 }, // Radio
        { wch: 12 }, // Mes
        { wch: 8 }, // Año
        { wch: 25 }, // Fecha de Creación
        { wch: 25 }, // Empresa
        { wch: 50 }, // URL PDF
      ];
      ws["!cols"] = colWidths;

      // Construir nombre de hoja de forma más compacta
      let nombreHoja = "Busqueda";

      // Agregar filtros de forma compacta
      const filtros = [];
      if (busquedaDni.trim()) {
        filtros.push(`DNI-${busquedaDni.trim()}`);
      }
      if (periodoFiltro) {
        filtros.push(getPeriodoEtiqueta(periodoFiltro));
      }

      if (filtros.length > 0) {
        nombreHoja += ` (${filtros.join(", ")})`;
      }

      // Truncar el nombre si excede 31 caracteres
      nombreHoja = truncarNombreHoja(nombreHoja);

      // Añadir hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

      // Descargar archivo con nombre actualizado
      XLSX.writeFile(wb, obtenerNombreArchivo("busqueda"));

      // Mostrar mensaje de éxito
      alert(`Excel descargado exitosamente con ${reportes.length} registro(s)`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al generar el archivo Excel");
    }
  };

  // Función helper para formatear fecha para Excel
  const formatearFechaExcel = (timestamp) => {
    if (!timestamp) return "N/A";

    const fecha = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return fecha.toLocaleDateString("es-PE", {
      timeZone: "America/Lima",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Función modificada para incluir filtro por período con mejor manejo de errores
  const obtenerDatosCompletos = async (periodo = null) => {
    if (scopeLoading) return;
    const requestId = ++activeRequestIdRef.current;
    setLoading(true);
    setError("");

    try {
      const chunkValues = (values, size = 10) => {
        const chunks = [];
        for (let i = 0; i < values.length; i += size) {
          chunks.push(values.slice(i, i + size));
        }
        return chunks;
      };

      // Obtener todos los usuarios de la empresa
      const qUsuarios = query(
        collection(db, "users"),
        where("rucEmpresa", "==", usuario.ruc)
      );

      const usuariosSnapshot = await getDocs(qUsuarios);
      const todosUsuarios = [];

      usuariosSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.dni) {
          todosUsuarios.push({
            id: doc.id,
            ...userData,
          });
        }
      });

      const usuariosPermitidos = filtrarPorDnisPermitidos(
        todosUsuarios,
        (item) => item?.dni,
        dnisPermitidos
      );
      setTodosLosUsuarios(usuariosPermitidos);
      const usuariosByDni = new Map(
        usuariosPermitidos.map((u) => [String(u.dni || "").trim(), u])
      );

      // Construir query para reportes
      let qReportes;
      let reportesSnapshot = null;
      const dnisScope =
        dnisPermitidos instanceof Set ? Array.from(dnisPermitidos) : null;

      if (dnisScope && dnisScope.length > 0) {
        // Optimización por alcance: consultar solo DNIs permitidos en lotes
        try {
          const [año, mes] = periodo ? periodo.split("-") : [];
          const mesNumerico = periodo ? parseInt(mes).toString() : null;
          const chunks = chunkValues(dnisScope, 10);
          const snapshots = await Promise.all(
            chunks.map(async (chunk) => {
              const constraints = [where("ruc", "==", usuario.ruc), where("dni", "in", chunk)];
              if (periodo) {
                constraints.push(where("año", "==", año), where("mes", "==", mesNumerico));
              }
              return getDocs(query(collection(db, "asistencia_reportes"), ...constraints));
            })
          );
          reportesSnapshot = {
            forEach: (cb) => snapshots.forEach((snap) => snap.forEach(cb)),
          };
        } catch (scopeQueryError) {
          console.warn("No se pudo aplicar query por alcance; se usará fallback general:", scopeQueryError?.message);
        }
      }

      if (!reportesSnapshot) {
        if (periodo) {
          const [año, mes] = periodo.split("-");
          // ✅ Convertir mes de "06" a "6" para que coincida con Firestore
          const mesNumerico = parseInt(mes).toString();

          qReportes = query(
            collection(db, "asistencia_reportes"),
            where("ruc", "==", usuario.ruc),
            where("año", "==", año),
            where("mes", "==", mesNumerico) // ✅ "6" en lugar de "06"
          );
        } else {
          qReportes = query(
            collection(db, "asistencia_reportes"),
            where("ruc", "==", usuario.ruc),
            orderBy("created_at", "desc")
          );
        }
        reportesSnapshot = await getDocs(qReportes);
      }
      const reportesData = [];

      reportesSnapshot.forEach((doc) => {
        const reporteData = doc.data();
        if (reporteData.dni) {
          reportesData.push({
            id: doc.id,
            ...reporteData,
          });
        }
      });
      const reportesDataPermitidos = filtrarPorDnisPermitidos(
        reportesData,
        (item) => item?.dni,
        dnisPermitidos
      );

      // Ordenar manualmente por fecha si hay filtro de período
      if (periodo) {
        reportesDataPermitidos.sort((a, b) => {
          const fechaA = a.created_at ? a.created_at.toDate() : new Date(0);
          const fechaB = b.created_at ? b.created_at.toDate() : new Date(0);
          return fechaB - fechaA;
        });
      }

      // Agrupar por DNI y obtener solo el último reporte de cada uno
      const reportesPorDni = {};
      reportesDataPermitidos.forEach((reporte) => {
        if (
          !reportesPorDni[reporte.dni] ||
          reporte.created_at.toDate() >
            reportesPorDni[reporte.dni].created_at.toDate()
        ) {
          reportesPorDni[reporte.dni] = reporte;
        }
      });

      const ultimosReportes = Object.values(reportesPorDni);

      // Optimización: evitar N consultas por reporte; resolver desde lista local de usuarios
      const reportesConDatos = ultimosReportes.map((reporte) => {
        const usuarioLocal = usuariosByDni.get(String(reporte.dni || "").trim());
        return {
          ...reporte,
          nombres: usuarioLocal?.name || reporte.nombres || "N/A",
          apellidos: usuarioLocal?.apellidos || reporte.apellidos || "N/A",
        };
      });

      if (requestId !== activeRequestIdRef.current) return;
      setReportes(reportesConDatos);

      // Optimización: combinar reportes con la lista local de usuarios (con fallback individual por DNI si hiciera falta)
      const conReportes = await Promise.all(
        ultimosReportes.map(async (reporte) => {
          const dniStr = String(reporte.dni || "").trim();
          const dniNorm = dniStr.replace(/\D/g, "");
          let usuarioLocal = usuariosByDni.get(dniStr) || usuariosPermitidos.find((u) => {
            const uDni = String(u.dni || u.legajo || u.legajo2 || "").trim();
            return uDni === dniStr || (uDni && uDni.replace(/\D/g, "") === dniNorm);
          });

          if (!usuarioLocal) {
            try {
              usuarioLocal = await obtenerDatosUsuario(dniStr);
            } catch (e) {}
          }

          const name = usuarioLocal?.nombres || usuarioLocal?.name || (reporte.nombres && reporte.nombres !== "N/A" ? reporte.nombres : "") || "";
          const apellidos = usuarioLocal?.apellidos || (usuarioLocal ? `${usuarioLocal.apellidoPaterno || ''} ${usuarioLocal.apellidoMaterno || ''}`.trim() : "") || (reporte.apellidos && reporte.apellidos !== "N/A" ? reporte.apellidos : "") || "";

          return {
            ...(usuarioLocal || {}),
            id: usuarioLocal?.id || reporte.id || dniStr,
            dni: dniStr,
            name: name || "Trabajador",
            nombres: name || "Trabajador",
            apellidos: apellidos || "",
            area: usuarioLocal?.area || usuarioLocal?.unidadOrganizativa || "",
            cargo: usuarioLocal?.cargo || usuarioLocal?.puesto || "",
            ultimoReporte: reporte,
            coordenadas: usuarioLocal?.centroNombre
              ? { nombrePlanta: usuarioLocal.centroNombre }
              : (reporte.coordenadas || null),
          };
        })
      );

      const dniConReportes = new Set(ultimosReportes.map((r) => String(r.dni || "").trim()));
      const sinReportes = usuariosPermitidos
        .filter((usuario) => !dniConReportes.has(String(usuario.dni || "").trim()))
        .map((usuario) => ({
          ...usuario,
          coordenadas: usuario.centroNombre
            ? { nombrePlanta: usuario.centroNombre }
            : null,
        }));

      if (requestId !== activeRequestIdRef.current) return;
      setUsuariosConReportes(conReportes);
      setUsuariosSinReportes(sinReportes);
    } catch (err) {
      if (requestId !== activeRequestIdRef.current) return;
      console.error("❌ Error al obtener datos:", err);
      setError("Error al cargar los datos de usuarios y reportes");
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Función modificada para búsqueda con filtro de período
  const buscarPorDni = async () => {
    if (!busquedaDni.trim()) {
      obtenerDatosCompletos(periodoFiltro);
      return;
    }

    const requestId = ++activeRequestIdRef.current;
    setLoading(true);
    setError("");

    try {
      // Limpiar el DNI de búsqueda
      const dniLimpio = busquedaDni.trim();
      if (dnisPermitidos instanceof Set && !dnisPermitidos.has(dniLimpio)) {
        if (requestId !== activeRequestIdRef.current) return;
        setReportes([]);
        setUsuariosConReportes([]);
        setUsuariosSinReportes([]);
        setError("No tienes permisos para consultar ese DNI.");
        return;
      }

      // PASO 1: Buscar reportes con filtro de período si está especificado
      let reportesData = [];

      if (periodoFiltro) {
        const [año, mes] = periodoFiltro.split("-");
        const mesNumerico = parseInt(mes).toString();
        
        const q = query(
          collection(db, "asistencia_reportes"),
          where("ruc", "==", usuario.ruc),
          where("dni", "==", dniLimpio),
          where("año", "==", año),
          where("mes", "==", mesNumerico)
        );

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          reportesData.push({
            id: doc.id,
            ...doc.data(),
          });
        });
      }

      // PASO 2: Si no encontró con filtro de período, buscar sin período para verificar si existe
      if (reportesData.length === 0) {
        console.log(`No hay reportes para el período ${periodoFiltro}. Buscando todos los reportes del usuario...`);
        
        const qSinPeriodo = query(
          collection(db, "asistencia_reportes"),
          where("ruc", "==", usuario.ruc),
          where("dni", "==", dniLimpio),
          orderBy("created_at", "desc"),
          limit(10) // Obtener los últimos 10 para mostrar opciones
        );

        const querySnapshotSinPeriodo = await getDocs(qSinPeriodo);
        querySnapshotSinPeriodo.forEach((doc) => {
          reportesData.push({
            id: doc.id,
            ...doc.data(),
          });
        });
      }

      // PASO 3: Si hay reportes, ordenar y tomar el más reciente
      if (reportesData.length > 0) {
        reportesData.sort((a, b) => {
          const fechaA = a.created_at ? a.created_at.toDate() : new Date(0);
          const fechaB = b.created_at ? b.created_at.toDate() : new Date(0);
          return fechaB - fechaA;
        });

        // Si hay período específico, filtrar solo de ese período
        if (periodoFiltro) {
          const [año, mes] = periodoFiltro.split("-");
          const mesNumerico = parseInt(mes).toString();
          reportesData = reportesData.filter(r => r.año === año && r.mes === mesNumerico);
        }

        if (reportesData.length > 0) {
          // Obtener datos del usuario para el reporte encontrado
          const reportesConDatos = await Promise.all(
            reportesData.slice(0, 1).map(async (reporte) => {
              try {
                const datosUsuario = await obtenerDatosUsuario(
                  reporte.dni,
                  todosLosUsuarios.length > 0
                    ? new Map(todosLosUsuarios.map((u) => [String(u.dni || "").trim(), u]))
                    : null
                );
                const coordenadas = await obtenerCoordenadasUsuario(reporte.dni);

                const nombreFinal = datosUsuario?.nombres || datosUsuario?.name || datosUsuario?.nombre || (reporte.nombres && reporte.nombres !== "N/A" ? reporte.nombres : "") || "Trabajador";
                const apellidosFinal = datosUsuario?.apellidos || (datosUsuario ? `${datosUsuario.apellidoPaterno || ''} ${datosUsuario.apellidoMaterno || ''}`.trim() : "") || (reporte.apellidos && reporte.apellidos !== "N/A" ? reporte.apellidos : "") || "";

                return {
                  ...(datosUsuario || {}),
                  id: datosUsuario?.id || reporte.id || reporte.dni,
                  dni: String(reporte.dni).trim(),
                  name: nombreFinal,
                  nombres: nombreFinal,
                  apellidos: apellidosFinal,
                  area: datosUsuario?.area || datosUsuario?.unidadOrganizativa || "",
                  cargo: datosUsuario?.cargo || datosUsuario?.puesto || "",
                  ultimoReporte: reporte,
                  coordenadas: coordenadas || (datosUsuario?.centroNombre ? { nombrePlanta: datosUsuario.centroNombre } : null),
                };
              } catch (error) {
                console.error(
                  `Error al obtener datos para DNI ${reporte.dni}:`,
                  error
                );
                return {
                  id: reporte.id || reporte.dni,
                  dni: String(reporte.dni).trim(),
                  name: reporte.nombres || "Trabajador",
                  nombres: reporte.nombres || "Trabajador",
                  apellidos: reporte.apellidos || "",
                  ultimoReporte: reporte,
                  coordenadas: null,
                };
              }
            })
          );

          if (requestId !== activeRequestIdRef.current) return;
          setReportes(reportesConDatos);
          setUsuariosConReportes(reportesConDatos);
          setUsuariosSinReportes([]);
          return;
        }
      }

      // PASO 4: Si no hay reportes, buscar datos del usuario y registros de marcación
      const datosUsuario = await obtenerDatosUsuario(
        dniLimpio,
        todosLosUsuarios.length > 0
          ? new Map(todosLosUsuarios.map((u) => [String(u.dni || "").trim(), u]))
          : null
      );
      
      // ✅ NUEVO: Verificar si existen registros de marcación incluso si el usuario no existe en DB
      let tieneRegistrosMarcacion = false;
      if (periodoFiltro) {
        const [año, mes] = periodoFiltro.split("-");
        console.log(`Verificando registros de marcación para DNI ${dniLimpio} en ${año}-${mes}...`);
        tieneRegistrosMarcacion = await verificarRegistrosAsistencia(dniLimpio, año, mes);
        console.log(`Resultado: ${tieneRegistrosMarcacion ? "SÍ hay registros" : "NO hay registros"}`);
      }
      
      if (datosUsuario || tieneRegistrosMarcacion) {
        const coordenadas = await obtenerCoordenadasUsuario(dniLimpio);
        
        let mensajeInfo = "Usuario encontrado ";
        
        if (datosUsuario && datosUsuario.cesado) {
          mensajeInfo += "(cesado/inactivo) ";
        }
        
        if (tieneRegistrosMarcacion) {
          mensajeInfo += `- Se encontraron registros de marcación para ${getPeriodoEtiqueta(periodoFiltro)}, pero falta generar el reporte`;
        } else if (periodoFiltro) {
          mensajeInfo += `pero sin reportes para ${getPeriodoEtiqueta(periodoFiltro)}`;
        } else {
          mensajeInfo += "pero sin reportes generados";
        }

        const nombreFinal = datosUsuario?.nombres || datosUsuario?.name || "Trabajador";
        const apellidosFinal = datosUsuario?.apellidos || "";
        const usuarioSinReporte = {
          dni: dniLimpio,
          name: nombreFinal,
          nombres: nombreFinal,
          apellidos: apellidosFinal,
          area: datosUsuario?.area || "",
          cargo: datosUsuario?.cargo || "",
          coordenadas: coordenadas,
          ultimoReporte: null,
          cesado: datosUsuario?.cesado || false,
          tieneRegistrosMarcacion: tieneRegistrosMarcacion,
          mensaje: mensajeInfo
        };

        if (requestId !== activeRequestIdRef.current) return;
        setReportes([]);
        setUsuariosConReportes([]);
        setUsuariosSinReportes([usuarioSinReporte]);
        setError(""); // Limpiar error ya que se encontró información
      } else {
        if (requestId !== activeRequestIdRef.current) return;
        // Usuario no existe en ningún lugar Y no hay registros de marcación
        setError(
          `No se encontró información para el DNI ${dniLimpio}${
            periodoFiltro
              ? ` en el período ${getPeriodoEtiqueta(periodoFiltro)}`
              : ""
          }. Verifica que el DNI sea correcto.`
        );
        setReportes([]);
        setUsuariosConReportes([]);
        setUsuariosSinReportes([]);
      }
    } catch (err) {
      if (requestId !== activeRequestIdRef.current) return;
      console.error("Error al buscar reporte:", err);
      setError("Error al buscar el reporte. Por favor intenta de nuevo.");
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Función para aplicar filtro de período
  const aplicarFiltroPeriodo = () => {
    if (busquedaDni.trim()) {
      buscarPorDni();
    } else {
      obtenerDatosCompletos(periodoFiltro); // ✅ Ya correcto
    }
  };

  // Función para limpiar todos los filtros
  const limpiarFiltros = () => {
    setBusquedaDni("");
    setPeriodoFiltro("");
    setGrupoSeleccionado(
      alcanceGrupo.restringidoPorGrupo
        ? grupos[0]?.id || alcanceGrupo.grupoAlcance?.id || "todos"
        : "todos"
    );
    // Llamar después de limpiar los filtros para que use el valor null
    setTimeout(() => {
      obtenerDatosCompletos(null); // ✅ Pasar null explícitamente
    }, 0);
  };

  // En generarPDFsGrupo, cambiar para requerir periodoFiltro y usar siempre ese valor
  const generarPDFsGrupo = async () => {
    if (!periodoFiltro) {
      alert("Selecciona un período en el filtro antes de generar los PDFs.");
      return;
    }

    const usuariosFiltrados = filtrarPorGrupo(todosLosUsuarios);

    if (usuariosFiltrados.length === 0) {
      alert("No hay usuarios en el grupo seleccionado para generar reportes.");
      return;
    }

    // Usar siempre el periodoFiltro seleccionado
    const [añoGenerar, mesGenerar] = periodoFiltro.split("-");

    setLoading(true);
    try {
      const { resultados, errores } = await generateControlAsistenciaMasiva(
        usuariosFiltrados,
        añoGenerar,
        mesGenerar,
        usuario.ruc,
        (msg) => console.log(`Progreso: ${msg}`)
      );

      alert(
        `Generación completada: ${resultados.length} exitosos, ${errores.length} errores. Revisa la consola para detalles.`
      );

      // Recargar datos para mostrar los nuevos reportes
      obtenerDatosCompletos(periodoFiltro);
    } catch (error) {
      console.error("Error al generar PDFs masivos:", error);
      alert("Error al generar los reportes PDF.");
    } finally {
      setLoading(false);
    }
  };

  const visualizarPDF = (reporte) => {
    try {
      // Abrir el PDF en una nueva pestaña
      window.open(reporte.pdf_url, "_blank");
    } catch (err) {
      console.error("Error al visualizar PDF:", err);
      setError("Error al abrir el archivo PDF");
    }
  };

  const formatearFecha = (timestamp) => {
    if (!timestamp) return "Fecha no disponible";

    const fecha = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return fecha.toLocaleDateString("es-PE", {
      timeZone: "America/Lima",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const limpiarBusqueda = () => {
    setBusquedaDni("");
    obtenerDatosCompletos(periodoFiltro); // ✅ Pasar el parámetro
  };

  return (
    <Container fluid className="py-4">
      <Card
        className="shadow-lg border-0"
        style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
      >
        {/* Header Premium con degradado verde oscuro */}
        <Card.Header
          className="text-white py-4 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
            borderBottom: "3px solid #f59e0b",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
          <div className="text-center">
            <h2 className="fw-bold mb-1">
              <FaCalendarAlt className="me-3" />
              Reportes de Asistencia PDF
            </h2>
            <p className="mb-0 opacity-75">
              Visualiza y genera los controles de asistencia mensuales firmados | RUC:{" "}
              {usuario?.ruc || "–"}
            </p>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
          {/* Acciones Rápidas */}
          <Card
            className="border-0 mb-4 shadow-sm"
            style={{
              background: "linear-gradient(135deg,#fffcea,#fff9db)",
              borderRadius: 14,
            }}
          >
            <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap align-items-center">
              <Stack direction="horizontal" gap={2} className="flex-wrap justify-content-center w-100">
                <GenerarReportePDF
                  ruc={usuario?.ruc || ""}
                  año={periodoFiltro ? periodoFiltro.split("-")[0] : undefined}
                  mes={periodoFiltro ? periodoFiltro.split("-")[1] : undefined}
                  usuariosSeleccionados={
                    activeTab === "sin-reportes"
                      ? usuariosSinReportes
                      : todosLosUsuarios
                  }
                  onReportesGenerados={() => obtenerDatosCompletos(periodoFiltro)}
                />

                <Button
                  variant="outline-dark"
                  onClick={() => obtenerDatosCompletos(periodoFiltro)}
                  disabled={loading}
                  className="d-flex align-items-center gap-2 bg-white"
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: "bold",
                    border: "2px solid #343a40"
                  }}
                >
                  <FaSync className={loading ? "fa-spin" : ""} size="16px" />
                  Actualizar Lista
                </Button>

                <Button
                  variant="success"
                  onClick={exportarConReportesExcel}
                  disabled={loading || reportes.length === 0}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    padding: "10px 20px",
                    fontWeight: "bold",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <FaFileExcel size="16px" />
                  Excel Masivo
                </Button>

                <Button
                  variant="dark"
                  onClick={descargarPDFsEnZip}
                  disabled={loading || reportes.length === 0}
                  className="d-flex align-items-center gap-2 shadow"
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: "bold",
                  }}
                >
                  <FaDownload size="16px" />
                  ZIP Masivo (PDFs)
                </Button>
              </Stack>
            </Card.Body>
          </Card>

          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              <FaTimesCircle className="me-2" />
              {error}
            </Alert>
          )}

          <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                buscarPorDni();
              }}
            >
              <Row className="g-3 align-items-end">
                {/* Búsqueda por DNI */}
                <Col md={4}>
                  <Form.Group controlId="busquedaDni">
                    <Form.Label className="fw-bold">
                      <FaSearch className="me-2" />
                      Buscar por DNI
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese DNI a buscar..."
                      value={busquedaDni}
                      onChange={(e) => setBusquedaDni(e.target.value)}
                      className="form-control-lg"
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>

                {/* Filtro por período */}
                <Col md={4}>
                  <Form.Group controlId="periodoFiltro">
                    <Form.Label className="fw-bold">
                      <FaFilter className="me-2" />
                      Filtrar por Período
                    </Form.Label>
                    <Form.Select
                      value={periodoFiltro}
                      onChange={(e) => setPeriodoFiltro(e.target.value)}
                      className="form-control-lg"
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="">Todos los períodos</option>
                      {generarOpcionesPeriodo().map((opcion) => (
                        <option key={opcion.valor} value={opcion.valor}>
                          {opcion.etiqueta}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Filtro por grupo */}
                <Col md={4}>
                  <Form.Group controlId="grupoFiltro">
                    <Form.Label className="fw-bold">
                      <FaUsers className="me-2" />
                      Filtrar por Grupo
                    </Form.Label>
                    <Form.Select
                      value={grupoSeleccionado}
                      onChange={(e) => setGrupoSeleccionado(e.target.value)}
                      className="form-control-lg"
                      style={{ borderRadius: "10px" }}
                      disabled={
                        scopeLoading ||
                        grupos.length === 0 ||
                        (alcanceGrupo.restringidoPorGrupo && grupos.length <= 1)
                      }
                    >
                      {!alcanceGrupo.restringidoPorGrupo && (
                        <option value="todos">Todos los grupos</option>
                      )}
                      {grupos.length === 0 && (
                        <option value="todos">No hay grupos disponibles</option>
                      )}
                      {grupos.map((grupo) => (
                        <option key={grupo.id} value={grupo.id}>
                          {grupo.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-center gap-2 mt-3">
                <Button 
                  variant="primary" 
                  onClick={aplicarFiltroPeriodo}
                  disabled={loading}
                  className="px-4 py-2 fw-bold shadow-sm d-flex align-items-center gap-2"
                  style={{ borderRadius: "10px" }}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaFilter className="me-1" />
                  )}
                  {loading ? "Buscando..." : "Aplicar Filtros"}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={limpiarFiltros}
                  disabled={loading}
                  className="px-4 py-2 shadow-sm"
                  style={{ borderRadius: "10px" }}
                >
                  <FaSync className="me-2" /> Limpiar
                </Button>
              </div>
            </Form>
          </div>

          {loading ? (
            <div className="text-center py-5 bg-white rounded-4 shadow-sm border mt-4">
              <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
              <h5 className="mt-3 text-primary fw-bold">Consultando reportes de asistencia...</h5>
              <p className="text-muted mb-0 small">Por favor espere un momento mientras se obtienen los datos.</p>
            </div>
          ) : (
            (() => {
              const conReportesFiltrados = obtenerDatosConReportesFiltrados();
              const sinReportesFiltrados = obtenerDatosSinReportesFiltrados();

              return (
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3 nav-tabs-custom mt-4"
                >
                  <Tab
                    eventKey="con-reportes"
                    title={
                      <span>
                        <FaCheckCircle className="me-2 text-success" />
                        Con Reporte ({conReportesFiltrados.length})
                      </span>
                    }
                  >
                    <div className="bg-white rounded-4 shadow-sm p-3 border">
                      <Table striped hover responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th className="border-0">Trabajador</th>
                            <th className="border-0">Área / Cargo / Planta</th>
                            <th className="border-0 text-center">Último Reporte</th>
                            <th className="border-0 text-center">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conReportesFiltrados.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center py-5 text-muted">
                                <FaInfoCircle size="30px" className="mb-2 opacity-50" />
                                <p>No hay reportes para el filtro seleccionado</p>
                              </td>
                            </tr>
                          ) : (
                            conReportesFiltrados.map((u) => {
                              const reporte = u.ultimoReporte;
                              return (
                                <tr key={u.dni}>
                                  <td>
                                    <div className="fw-bold">
                                      {formatNombreTrabajador(u)}
                                    </div>
                                    <div className="text-muted small">DNI: {u.dni}</div>
                                  </td>
                                  <td>
                                    <div>{u.area || u.unidadOrganizativa || "Sin área"}</div>
                                    <div className="text-muted small">
                                      {u.cargo || "--"} | {u.coordenadas?.nombrePlanta || "Sin Planta"}
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <Badge bg="success" className="px-3 py-2">
                                      {formatUltimoReporteFecha(reporte, periodoFiltro)}
                                    </Badge>
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex justify-content-center align-items-center gap-2">
                                      {reporte?.pdf_url && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => visualizarPDF(reporte)}
                                          className="d-flex align-items-center gap-1"
                                          style={{ borderRadius: "8px" }}
                                        >
                                          <FaEye /> Ver PDF
                                        </Button>
                                      )}
                                      <GenerarReportePDF
                                        singleUser={u}
                                        ruc={usuario?.ruc || ""}
                                        año={periodoFiltro ? periodoFiltro.split("-")[0] : (reporte?.año || undefined)}
                                        mes={periodoFiltro ? periodoFiltro.split("-")[1] : (reporte?.mes || undefined)}
                                        onReportesGenerados={() =>
                                          obtenerDatosCompletos(periodoFiltro)
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab>

                  <Tab
                    eventKey="sin-reportes"
                    title={
                      <span>
                        <FaTimesCircle className="me-2 text-danger" />
                        Sin Reporte ({sinReportesFiltrados.length})
                      </span>
                    }
                  >
                    <div className="bg-white rounded-4 shadow-sm p-3 border">
                      <Table striped hover responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th className="border-0">Trabajador</th>
                            <th className="border-0">Área / Cargo / Planta</th>
                            <th className="border-0 text-center">Estado</th>
                            <th className="border-0 text-center">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sinReportesFiltrados.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center py-5 text-muted">
                                <FaCheckCircle
                                  size="30px"
                                  className="mb-2 text-success opacity-50"
                                />
                                <p>Todos los trabajadores seleccionados tienen reporte</p>
                              </td>
                            </tr>
                          ) : (
                            sinReportesFiltrados.map((u) => (
                              <tr key={u.dni}>
                                <td>
                                  <div className="fw-bold">
                                    {formatNombreTrabajador(u)}
                                  </div>
                                  <div className="text-muted small">DNI: {u.dni}</div>
                                </td>
                                <td>
                                  <div>{u.area || "Sin área"}</div>
                                  <div className="text-muted small">
                                    {u.cargo || "--"} | {u.coordenadas?.nombrePlanta || "Sin Planta"}
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Badge bg="warning" text="dark" className="px-3 py-2">
                                    Pendiente
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <GenerarReportePDF
                                    singleUser={u}
                                    ruc={usuario?.ruc || ""}
                                    año={periodoFiltro ? periodoFiltro.split("-")[0] : undefined}
                                    mes={periodoFiltro ? periodoFiltro.split("-")[1] : undefined}
                                    onReportesGenerados={() =>
                                      obtenerDatosCompletos(periodoFiltro)
                                    }
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab>
                </Tabs>
              );
            })()
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ReporteAsistenciaPDF;
export { generateControlAsistencia }; // ✅ Cambiar a generateControlAsistencia
